import { useCallback, useEffect } from 'react'

/**
 * Custom hook to make the header row of a DataGrid sticky when scrolling.
 * It adjusts the top position of the header based on the scroll position.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {React.RefObject<HTMLElement | null>} params.containerRef - The reference to the DataGrid container.
 * @param {number} params.topOffset - The offset from the top of the window to consider for stickiness.
 * @param {number} params.extraPadding - Additional padding to add to the scrolling calculation, useful for when there is extra space between the banner and column header.
 */
const useStickyHeaders = ({
  containerRef,
  topOffset = 184,
  extraPadding = 24,
}: {
  containerRef: React.RefObject<HTMLElement | null>
  topOffset?: number
  extraPadding?: number
}) => {
  const handleScroll = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    // Find the required elements
    const virtualScroller = container.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement
    const topContainer = container.querySelector('.MuiDataGrid-topContainer') as HTMLElement
    const toolbar = container.querySelector('.MuiDataGrid-toolbar') as HTMLElement

    if (!virtualScroller || (!topContainer && !toolbar)) return

    // Make the columns header sticky if the scroll position is greater than the top offset + height of the columns header
    if (window.scrollY >= topOffset + topContainer.clientHeight + extraPadding) {
      if (topContainer) {
        topContainer.style.position = 'fixed'
        topContainer.style.top = `${topOffset}px`
      }
      if (toolbar) {
        toolbar.style.position = 'fixed'
        toolbar.style.top = `${topOffset}px`
        toolbar.style.left = '0'
      }
      container.style.transform = 'none'
      virtualScroller.style.paddingTop = `${topContainer.clientHeight}px`
    } else {
      // Reset styles when not sticky
      if (topContainer) {
        topContainer.style.position = ''
        topContainer.style.top = ''
      }
      if (toolbar) {
        toolbar.style.position = ''
        toolbar.style.top = ''
        toolbar.style.left = ''
      }
      container.style.transform = ''
      virtualScroller.style.paddingTop = ''
    }
  }, [containerRef, extraPadding, topOffset])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Initial check
      // wrapped in timeout to allow DOM elements to fully render before measuring their position
      handleScroll()
    }, 100)

    // Add scroll event listeners
    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleScroll)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [containerRef, handleScroll, topOffset])

  return { doStickyHeaders: handleScroll }
}

export default useStickyHeaders
